import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCart } from '../contexts/CartContext';
import { useKiosk } from '../contexts/KioskContext';
import { trackClick } from '../services/analyticsService';
import { useLocale } from '../hooks/useLocale';
import { formatPrice as fmtPrice } from '../utils/format';
import { tField } from '../utils/i18nContent';
import { productListing as proxyCard } from '../lib/imageProxy';
import BlurImage from './BlurImage';
import { ADD_TO_CART_FEEDBACK_MS } from '../constants';

const ProductCard = ({ product }) => {
  const { addToCart } = useCart();
  const { isKiosk } = useKiosk();
  const locale = useLocale();
  const { t } = useTranslation('product');
  const displayName = tField(product, 'name', locale) || product.name || '';
  const [justAdded, setJustAdded] = useState(false);
  const addedTimerRef = useRef(null);

  useEffect(() => () => clearTimeout(addedTimerRef.current), []);

  // Check if product is sold out (from database)
  const isSoldOut = product.isSoldOut || false;

  const handleAddToCart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isSoldOut) {
      trackClick(`add-to-cart-${product.id}`, 'button', 'Agregar al Carrito', {
        productId: product.id,
        productName: product.name,
        productPrice: product.price
      });
      addToCart(product);
      setJustAdded(true);
      clearTimeout(addedTimerRef.current);
      addedTimerRef.current = setTimeout(() => setJustAdded(false), ADD_TO_CART_FEEDBACK_MS);
    }
  };

  const handleCardClick = (e) => {
    if (!isSoldOut) {
      trackClick(`product-card-${product.id}`, 'link', product.name, {
        productId: product.id,
        productName: product.name
      });
    } else {
      e.preventDefault();
    }
  };

  const formatPrice = (price) => {
    if (!price || isNaN(price)) {
      return '$0.00';
    }
    return fmtPrice(price, 'MXN', locale);
  };

  // Get the correct price to display (preorder: use preorderPrice if set, else fallback to price)
  const displayPrice = product.isPreorder && (product.preorderPrice != null && product.preorderPrice !== '')
    ? product.preorderPrice
    : product.price;
  
  if (!product || !product.id) {
    console.error('Invalid product:', product);
    return null;
  }

  return (
    <Link 
      to={`/product/${product.slug || product.id}`}
      className={`product-card ${isSoldOut ? 'product-sold-out' : ''}`}
      onClick={handleCardClick}
      style={{
        position: 'relative',
        border: product.specifications?.restrictions ? '2px solid #2ca9e1' : 'none',
        pointerEvents: isSoldOut ? 'none' : 'auto'
      }}
    >
      {product.specifications?.restrictions && (
        <div style={{
          position: 'absolute',
          top: '10px',
          right: '10px',
          background: '#2ca9e1',
          color: 'white',
          padding: '0.25rem 0.5rem',
          borderRadius: '4px',
          fontSize: '0.75rem',
          fontWeight: '600',
          zIndex: 2
        }}>
          EXCLUSIVE
        </div>
      )}
      {isSoldOut && (
        <div style={{
          position: 'absolute',
          top: '10px',
          left: '10px',
          background: '#dc3545',
          color: 'white',
          padding: '0.5rem 1rem',
          borderRadius: '4px',
          fontSize: '0.875rem',
          fontWeight: '700',
          zIndex: 2,
          textTransform: 'uppercase',
          letterSpacing: '0.5px'
        }}>
          {t('soldOut')}
        </div>
      )}
      {isKiosk && product.isInTransit && !isSoldOut && (
        <div style={{
          position: 'absolute',
          top: '10px',
          right: '10px',
          background: '#007bff',
          color: 'white',
          padding: '0.5rem 1rem',
          borderRadius: '4px',
          fontSize: '0.875rem',
          fontWeight: '700',
          zIndex: 2,
          textTransform: 'uppercase',
          letterSpacing: '0.5px'
        }}>
          {t('inTransit')}
        </div>
      )}
      <div className={`product-image ${isSoldOut ? 'product-image-grayscale' : ''}`}>
        <BlurImage
          src={proxyCard(product.image)}
          alt={displayName}
          loading="lazy"
          objectFit="contain"
          data-product-id={product.id}
          onError={(e) => {
            e.currentTarget.src = 'https://via.placeholder.com/300x250/f8f9fa/333?text=Product+Image';
          }}
        />
      </div>
      <div className="product-info" style={{ pointerEvents: 'auto' }}>
        <h3 className="product-name">{displayName}</h3>
        <p className="product-price">{formatPrice(displayPrice)}</p>
        <button
          className={`btn btn-primary${justAdded ? ' btn-just-added' : ''}`}
          onClick={handleAddToCart}
          disabled={isSoldOut}
          style={{
            width: '100%',
            opacity: isSoldOut ? 0.5 : 1,
            cursor: isSoldOut ? 'not-allowed' : 'pointer',
            pointerEvents: 'auto',
            marginTop: 'auto'
          }}>
          {isSoldOut ? t('soldOut') : justAdded ? (
            <span style={{ fontWeight: 700 }}>{t('added')}</span>
          ) : t('addToCart')}
        </button>
      </div>
    </Link>
  );
};

export default ProductCard;
